import {ChangeDetectionStrategy, Component, Input} from '@angular/core';
import {MatIconModule} from '@angular/material/icon';
import {EventBusService} from '../../../../service/event-bus.service';
import {EventBusTypes} from '../../../../model/constants';
import {CosmoUebernahme, GrafikUmfang} from '../../../../model/event-bus.model';
import {KartenZoomComponent} from '../../../common/karten-zoom/karten-zoom.component';
import {KiErgebnisBoxComponent} from '../../../common/ki-ergebnis-box/ki-ergebnis-box.component';
import {KiLaufAnzeigeComponent} from '../../../common/ki-lauf-anzeige/ki-lauf-anzeige.component';
import {LadeQuellenBoxComponent} from '../../../common/lade-quellen-box/lade-quellen-box.component';
import {LadeQuellenService} from '../../../../service/lade-quellen.service';
import {ForecastSnapshotService} from '../../../../service/http/forecast-snapshot.service';
import {StationForecastService} from '../../../../service/http/station-forecast.service';
import {MeteoMeasurementsService} from '../../../../service/http/meteo-measurements.service';
import {KiGrafikService} from '../../../../service/ki-grafik.service';
import {SchlagzeileSchalterService} from '../../../../service/schlagzeile-schalter.service';

/** Ein Button der Kopfleiste. Rein deklarativ – die Aktionen sind noch nicht verdrahtet. */
export interface ToolbarAction {
  /** Beschriftung des Buttons. */
  label: string;
  /** Material-Icon links im Button. */
  icon?: string;
  /** Statt eines Icons das gelbe "AI"-Kürzel zeigen. */
  ai?: boolean;
  /** Grüne Variante (KI-Aktion) statt der blauen Standardfarbe. */
  ki?: boolean;
  /** Event, das der Button auf dem Event-Bus auslöst. Ohne Angabe bleibt er ohne Funktion. */
  event?: string;
  /**
   * Der KI-Knopf nimmt hier **alle** Zeilen, nicht nur die mit angehaktem Symbol – weil
   * seine Karten keine Umschalter haben (Lokalzeit-Prognose). Blendet den Hinweis
   * "nur aus Zeilen mit angehaktem Symbol" aus, der sonst irreführend wäre.
   */
  ohneHaken?: boolean;
}

/** Kopfleiste über den Grafik-Zeilen. */
export const GRAFIK_TOOLBAR: { title: string; actions: ToolbarAction[] } = {
  title: 'Nordrhein-Westfalen: Grafiken Basis für KI-Texte',
  actions: [
    {label: 'MSWR-MOS laden', icon: 'download', event: EventBusTypes.MSWR_LOAD_EVENT},
    {label: 'Speichern', icon: 'save', event: EventBusTypes.SAVE_EVENT},
    {label: 'für COSMO übernehmen', icon: 'publish', event: EventBusTypes.COSMO_TAKEOVER_EVENT},
    {label: 'für TV-Sendungen übernehmen', icon: 'publish'},
    {label: 'KI-Texte erzeugen', ai: true, ki: true, event: EventBusTypes.KI_GRAFIK_EVENT},
  ]
};

/** Kopfleiste des Reiters "KI-Texte". */
export const KI_TEXTE_TOOLBAR: { title: string; actions: ToolbarAction[] } = {
  title: 'Nordrhein-Westfalen: editierbare KI-Texte',
  actions: [
    {label: 'Speichern', icon: 'save', event: EventBusTypes.SAVE_EVENT},
    {label: 'für Videotext übernehmen', icon: 'publish', event: EventBusTypes.VIDEOTEXT_TAKEOVER_EVENT},
    {label: 'für COSMO übernehmen', icon: 'publish', event: EventBusTypes.COSMO_TEXT_TAKEOVER_EVENT},
  ]
};

/**
 * Kopfleiste über den Grafik-Zeilen von COSMO.
 *
 * Eigene Konstante statt der von Nordrhein-Westfalen: der Titel nennt den Bereich, und
 * COSMO hat die beiden Übernehmen-Knöpfe nicht – dort ist COSMO ja selbst das Ziel.
 * "KI-Texte erzeugen" ist derselbe Knopf wie in NRW (`KI_GRAFIK_EVENT`); wohin der Text
 * geht, entscheidet `KiGrafikService.ZIELE` über das Präfix `wdr.cosmo.` der Karten.
 */
export const COSMO_GRAFIK_TOOLBAR: { title: string; actions: ToolbarAction[] } = {
  title: 'COSMO: Grafiken Basis für KI-Texte',
  actions: [
    {label: 'MSWR-MOS laden', icon: 'download', event: EventBusTypes.MSWR_LOAD_EVENT},
    {label: 'Speichern', icon: 'save', event: EventBusTypes.SAVE_EVENT},
    {label: 'KI-Texte erzeugen', ai: true, ki: true, event: EventBusTypes.KI_GRAFIK_EVENT},
  ]
};

/**
 * Kopfleiste über den Grafik-Zeilen der Lokalzeiten (Heute … 3.Aussichtentag).
 *
 * Ohne "für COSMO übernehmen": eine Lokalzeit-Grafik betrifft eine Stadt, COSMO sendet für
 * NRW, Bremen und Berlin — die Übernahme dorthin hat hier keinen Sinn.
 * "KI-Texte erzeugen" ist derselbe Knopf wie in NRW (`KI_GRAFIK_EVENT`); Ziel ist die
 * Textbox des Zeitraums im Reiter "KI-Texte" der Stadt, aus "Heute" zusätzlich deren
 * Schlagzeile (`KiGrafikService.ZIELE` / `SCHLAGZEILEN`, Präfix `wdr.lokalzeit.<stadt>.`).
 */
export const LOKALZEIT_GRAFIK_TOOLBAR: { title: string; actions: ToolbarAction[] } = {
  title: 'Lokalzeit: Grafiken Basis für KI-Texte',
  actions: [
    {label: 'MSWR-MOS laden', icon: 'download', event: EventBusTypes.MSWR_LOAD_EVENT},
    {label: 'Speichern', icon: 'save', event: EventBusTypes.SAVE_EVENT},
    {label: 'für TV-Sendungen übernehmen', icon: 'publish'},
    {label: 'KI-Texte erzeugen', ai: true, ki: true, event: EventBusTypes.KI_GRAFIK_EVENT},
  ]
};

/**
 * Kopfleiste über den Grafik-Zeilen von "Lokalzeit am Samstag" – ohne verdrahteten KI-Knopf:
 * ihr Reiter "KI-Texte" ist anders gebaut (Nacht, Morgen Vormittag/Nachmittag, eine
 * gemeinsame Aussichten-Box, keine Heute- und keine Schlagzeilen-Box) und hat noch kein Ziel
 * in `KiGrafikService.ZIELE`.
 */
export const LOKALZEIT_SAMSTAG_GRAFIK_TOOLBAR: { title: string; actions: ToolbarAction[] } = {
  title: 'Lokalzeit am Samstag: Grafiken',
  actions: [
    {label: 'MSWR-MOS laden', icon: 'download', event: EventBusTypes.MSWR_LOAD_EVENT},
    {label: 'Speichern', icon: 'save', event: EventBusTypes.SAVE_EVENT},
    {label: 'für TV-Sendungen übernehmen', icon: 'publish'},
    {label: 'KI-Texte generieren', ai: true, ki: true},
  ]
};

/** Beschriftung des Lokalzeit-Reiters, dessen Leiste den verdrahteten KI-Knopf trägt. */
export const LOKALZEIT_PROGNOSE_REITER = 'Tendenzen/Prognosen';

/**
 * Kopfleiste des Lokalzeit-Reiters "Tendenzen/Prognosen".
 *
 * Als einzige Lokalzeit-Leiste mit verdrahtetem KI-Knopf: aus der Prognose-Karte (Wind,
 * Regen, Sonne je Tag) entsteht der Text der TV-Vorhersage-Box "Wind, Regen, Sonne" der
 * Stadt (`KiGrafikService.LOKALZEIT_PROGNOSE`). In den übrigen Lokalzeit-Reitern bleibt
 * der Knopf ohne Funktion, weil es dort noch kein Ziel gibt. `ohneHaken`: die
 * Prognose-Boxen haben keine Umschalter, der Knopf nimmt die Zeile immer mit.
 * "gespeicherte Daten laden" (November 2026) steht rechts neben "MSWR-MOS laden": die
 * TV-Werte der drei Aussichtentage (Zeile "ganz", Karte 3) wandern in die Spalten Tag 2/3/4
 * der Prognose-Karte (`PrognoseUebernahmeService`, Event mit dem Umfang der Leiste).
 */
export const LOKALZEIT_PROGNOSE_TOOLBAR: { title: string; actions: ToolbarAction[] } = {
  title: 'Lokalzeit: Prognose Basis für "Wind, Regen, Sonne"',
  actions: [
    {label: 'MSWR-MOS laden', icon: 'download', event: EventBusTypes.MSWR_LOAD_EVENT},
    {label: 'gespeicherte Daten laden', icon: 'input', event: EventBusTypes.PROGNOSE_UEBERNAHME_EVENT},
    {label: 'Speichern', icon: 'save', event: EventBusTypes.SAVE_EVENT},
    {label: 'KI-Texte erzeugen', ai: true, ki: true, event: EventBusTypes.KI_GRAFIK_EVENT, ohneHaken: true},
  ]
};

/** Kopfleiste des Lokalzeit-Reiters "KI-Texte" – ebenfalls ohne COSMO-Übernahme. */
export const LOKALZEIT_KI_TEXTE_TOOLBAR: { title: string; actions: ToolbarAction[] } = {
  title: 'Lokalzeit: editierbare KI-Texte',
  actions: [
    {label: 'für Videotext übernehmen', icon: 'publish'},
  ]
};

/** Kopfleiste des COSMO-Reiters "KI-Texte". */
export const COSMO_KI_TEXTE_TOOLBAR: { title: string; actions: ToolbarAction[] } = {
  title: 'COSMO: editierbare KI-Texte',
  actions: [
    {label: 'für Videotext übernehmen', icon: 'publish'},
  ]
};

/**
 * Knopfleiste einer Grafik-/KI-Seite – **zweimal auf jeder Seite**, mit demselben
 * Knopfsatz und unterschiedlicher Reichweite:
 *
 * - `kompakt = false` (Vorgabe): die Leiste **oben**, mit Überschrift. Ihre Knöpfe gelten
 *   für die ganze Seite: "KI-Texte erzeugen" sammelt die angehakten Boxen **aller**
 *   Zeilen ein, "MSWR-MOS laden" füllt alle Karten. Sie zeigt darunter die grüne Box mit
 *   ihrer eigenen Antwort.
 * - `kompakt = true`: die Leiste **an einer Zeilenüberschrift**. Sie schickt die
 *   `containerId`s genau ihrer Karten mit, wirkt also nur dort. Die grüne Box dieser
 *   Zeile rendert die Zeile selbst, gleich unter der Überschrift.
 *
 * Den Unterschied macht allein der `kartenIds`-Umfang im Event ({@link GrafikUmfang}) –
 * die Knopf-Definitionen sind dieselben Konstanten.
 *
 * Verdrahtet sind "MSWR-MOS laden", "Speichern", "für COSMO übernehmen" (Grafik-Werte),
 * "KI-Texte erzeugen" (in NRW und COSMO) sowie – im Reiter "KI-Texte" von Nordrhein-Westfalen – "für
 * Videotext übernehmen" und "für COSMO übernehmen" (dort: Texte, ein stumpfer
 * Kopiervorgang ohne KI-Aufruf). "für TV-Sendungen übernehmen" ist noch reines Design,
 * ebenso die gleichnamigen Knöpfe von COSMO und Lokalzeit im Reiter "KI-Texte".
 */
@Component({
  selector: 'app-graphic-toolbar',
  templateUrl: './graphic-toolbar.component.html',
  styleUrls: ['./graphic-toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatIconModule, KartenZoomComponent, KiErgebnisBoxComponent, KiLaufAnzeigeComponent, LadeQuellenBoxComponent]
})
export class GraphicToolbarComponent {

  /**
   * Die Events, die auf Karten wirken und deshalb den {@link GrafikUmfang} tragen.
   * Alle anderen betreffen den ganzen Datensatz und brauchen ihn nicht.
   */
  private static readonly KARTEN_AKTIONEN: string[] = [
    EventBusTypes.MSWR_LOAD_EVENT.valueOf(),
    EventBusTypes.CLEAR_BOXES_EVENT.valueOf(),
    EventBusTypes.KI_GRAFIK_EVENT.valueOf(),
    EventBusTypes.PROGNOSE_UEBERNAHME_EVENT.valueOf()
  ];

  @Input() title = '';
  @Input() actions: ToolbarAction[] = [];
  /** Zeigt die Zoom-Bedienung – nur sinnvoll auf Seiten mit Karten. */
  @Input() showZoom = false;
  /**
   * Beschriftung des Reiters, in dem diese Leiste steht ("Heute", "Nacht", ...).
   *
   * Wird beim Event mitgeschickt: "für COSMO übernehmen" betrifft nur den gerade
   * geöffneten Reiter, und die Leiste ist die einzige Stelle, die ihn kennt.
   */
  @Input() reiter = '';
  /**
   * Zeilen-Variante: ohne Überschrift, kleinere Knöpfe, ohne eigene grüne Box.
   * Gesetzt von der Knopfreihe an einer Zeilenüberschrift.
   */
  @Input() kompakt = false;
  /**
   * Wie weit diese Leiste reicht: über ihren Reiter (die Leiste oben) oder über eine
   * einzelne Zeile (die Knopfreihe an einer Zeilenüberschrift).
   *
   * ⚠️ Muss ein **stabiles** Objekt sein – bei jedem Aufruf neu gebaut löste es NG0100
   * aus. Die Elemente-Komponente merkt sich ihre Umfänge deshalb.
   */
  @Input() umfang: GrafikUmfang | null = null;

  constructor(private eventBusService: EventBusService,
              private ladeQuellenService: LadeQuellenService,
              private stationForecastService: StationForecastService,
              private forecastSnapshotService: ForecastSnapshotService,
              private measurementsService: MeteoMeasurementsService,
              private kiGrafik: KiGrafikService,
              private schlagzeileSchalter: SchlagzeileSchalterService) {
  }

  /**
   * Die Nacht-Textbox dieses Reiters, falls er ein Nacht-Reiter mit Schlagzeile ist
   * (NRW, Lokalzeit-Städte) – der Schlüssel des Schalters "Schlagzeile erzeugen"; sonst null.
   * Erkannt über die Karten des Umfangs, nicht über die Beschriftung "Nacht", die es in
   * COSMO (ohne Schlagzeile) ebenso gibt.
   */
  get nachtZiel(): string | null {
    if (!this.umfang) {
      return null;
    }
    const zielId = this.kiGrafik.zielFuer(this.umfang);
    return KiGrafikService.nachtSchlagzeileZu(zielId) ? zielId : null;
  }

  /**
   * Zeigt diese Leiste den Schalter "Schlagzeile erzeugen"? Nur die Leiste oben eines
   * Nacht-Reiters mit verdrahtetem KI-Knopf. Der Zeilen-Knopf braucht keinen eigenen: er
   * schickt ohnehin den Reiter-Umfang, der Schalter gilt also auch für ihn.
   */
  get zeigtSchlagzeilenSchalter(): boolean {
    return !this.kompakt && this.hatKiAktion && this.nachtZiel !== null;
  }

  /** Steht der Schalter "Schlagzeile erzeugen" dieses Nacht-Reiters an? (Signal, Standard aus) */
  get schlagzeileAn(): boolean {
    return this.schlagzeileSchalter.istAn(this.nachtZiel);
  }

  onSchlagzeileToggle(): void {
    const ziel = this.nachtZiel;
    if (ziel) {
      this.schlagzeileSchalter.umschalten(ziel);
    }
  }

  get schlagzeileTitel(): string {
    return this.schlagzeileAn
      ? 'Schlagzeile erzeugen: an – "KI-Texte erzeugen" schreibt zusätzlich zum Nacht-Text eine Schlagzeile '
        + 'aus den Nacht-Daten in die Schlagzeilen-Box, wie im Reiter "Heute"'
      : 'Schlagzeile erzeugen: aus – "KI-Texte erzeugen" schreibt nur den Nacht-Text. '
        + 'Eingeschaltet entsteht zusätzlich eine Schlagzeile aus den Nacht-Daten, wie im Reiter "Heute"';
  }

  /**
   * Zeigt diese Leiste die grüne Box mit der KI-Antwort?
   *
   * Nur die Leiste oben, und nur wenn sie den verdrahteten Knopf trägt: die Leiste der
   * Lokalzeit hat einen ähnlich benannten Knopf, der noch keine Anfrage auslöst,
   * und dort wäre eine Antwort irreführend. Die Boxen der Zeilen rendern die Zeilen
   * selbst – sie stehen zwischen Überschrift und Karten, nicht in der Knopfreihe.
   */
  get zeigtErgebnis(): boolean {
    return !this.kompakt && this.hatKiAktion;
  }

  /**
   * Hinweis in der Leiste oben, dass der KI-Knopf nur Zeilen mit angehaktem Symbol nimmt –
   * nicht bei einem Knopf `ohneHaken` (Lokalzeit-Prognose, Boxen ohne Umschalter).
   */
  get zeigtKiHinweis(): boolean {
    return !this.kompakt && this.actions.some(action =>
      action.event === EventBusTypes.KI_GRAFIK_EVENT.valueOf() && !action.ohneHaken);
  }

  /** Ist das der Knopf, der eine KI-Anfrage auslöst? Neben ihm steht die Fortschrittsanzeige. */
  istKiAktion(action: ToolbarAction): boolean {
    return action.event === EventBusTypes.KI_GRAFIK_EVENT.valueOf();
  }

  /**
   * Schlüssel der Fortschrittsanzeige: derselbe Umfang, den {@link onAction} mit dem Event
   * schickt – `KiLaufService` legt den Lauf unter genau diesem Schlüssel an.
   */
  get laufSchluessel(): string {
    return (this.umfang ?? GrafikUmfang.alles()).schluessel;
  }

  /** Gehört diese Leiste zu einer einzelnen Zeile? Dann nennen die Titel sie. */
  private get zeile(): string {
    return this.umfang?.art === 'zeile' ? this.umfang.name : '';
  }

  /** Trägt diese Leiste den Knopf, der die KI-Anfrage wirklich auslöst? */
  private get hatKiAktion(): boolean {
    return this.actions.some(action => action.event === EventBusTypes.KI_GRAFIK_EVENT.valueOf());
  }

  /**
   * Titel eines Knopfes: in einer Zeilen-Leiste nennt er den Umfang, damit am kleinen
   * Knopf ablesbar bleibt, wie weit er reicht.
   *
   * Wichtig für "Speichern": es steht neben zeilenweise wirkenden Knöpfen, tut aber
   * etwas anderes – deshalb sagt sein Titel ausdrücklich, dass der ganze Datensatz
   * gemeint ist. Ein "nur Zeile ..." dort wäre schlicht falsch.
   */
  knopfTitel(action: ToolbarAction): string {
    // Der KI-Knopf wirkt in jeder Leiste gleich – der an einer Zeile ist nur ein zweiter
    // Zugang zum Knopf oben (DocumentDetailComponent.reiterUmfangVon).
    if (action.event === EventBusTypes.KI_GRAFIK_EVENT.valueOf()) {
      return this.zeile
        ? `${action.label} – wie der Knopf oben: alle Zeilen dieses Reiters mit angehaktem `
          + 'Symbol (gelbes Kästchen), Antwort erscheint oben'
        : `${action.label} – nur Zeilen mit angehaktem Symbol (gelbes Kästchen)`;
    }
    if (action.event === EventBusTypes.PROGNOSE_UEBERNAHME_EVENT.valueOf()) {
      return `${action.label} – die TV-Werte der drei Aussichtentage (Zeile 'ganz': Wind, Regen, Sonne, `
        + 'Höchstwert und Symbol des Hauptorts) in die Spalten Tag 2/3/4 der Prognose übernehmen; '
        + 'vorhandene Werte dort werden überschrieben';
    }
    if (!this.zeile || !action.event) {
      return action.label;
    }
    if (action.event === EventBusTypes.SAVE_EVENT.valueOf()) {
      return `${action.label} – legt den ganzen Datensatz ab`;
    }
    return this.kenntUmfang(action.event)
      ? `${action.label} – nur Zeile "${this.zeile}"`
      : action.label;
  }

  /** Wirkt diese Aktion auf einen Umfang, oder immer auf den ganzen Datensatz? */
  private kenntUmfang(event: string): boolean {
    return GraphicToolbarComponent.KARTEN_AKTIONEN.includes(event)
      || event === EventBusTypes.COSMO_TAKEOVER_EVENT.valueOf();
  }

  /**
   * Löst das hinterlegte Event aus; Buttons ohne Event bleiben wirkungslos.
   *
   * Die Nutzlast unterscheidet sich je Event, weil die Empfänger Verschiedenes brauchen:
   * die Karten-Aktionen den {@link GrafikUmfang}, die COSMO-Übernahme den Reiter (und
   * bei einer Zeilen-Leiste zusätzlich die Zeile), "Speichern" nichts davon – der
   * Datensatz ist immer ganz.
   */
  onAction(action: ToolbarAction): void {
    if (!action.event) {
      return;
    }
    if (action.event === EventBusTypes.MSWR_LOAD_EVENT.valueOf()) {
      // Gemerkte Antworten verwerfen, damit der Klick wirklich neu lädt und nicht
      // nur die beim Seitenaufbau geholten Werte erneut einträgt – beide Ladewege,
      // damit der Button unabhängig von environment.useForecastSnapshot wirkt.
      this.forecastSnapshotService.reload();
      this.stationForecastService.clearCache();
      // Dritter Ladeweg: die Lokalzeit-Karten holen ihre Werte aus den MOS-Dateien der
      // Wetterdaten-API, nicht über Stationen. Auch deren Cache muss weg, sonst zeigt
      // der Knopf die Datei von vorhin.
      this.measurementsService.clearCache();
      // Der Kasten "MSWR-MOS geladen" dieses Knopfes: alten Bericht weg, neuen anlegen – die
      // Karten des Umfangs melden gleich, woraus sie geladen haben.
      this.ladeQuellenService.starte(this.umfang);
    }
    if (GraphicToolbarComponent.KARTEN_AKTIONEN.includes(action.event)) {
      this.eventBusService.emit(action.event, this.umfang ?? GrafikUmfang.alles());
      return;
    }
    if (action.event === EventBusTypes.COSMO_TAKEOVER_EVENT.valueOf()) {
      this.eventBusService.emit(action.event, new CosmoUebernahme(this.reiter, this.zeile || null));
      return;
    }
    // Alles Übrige – vor allem "Speichern" – kennt **keinen** Umfang und bekommt deshalb
    // auch keinen: der Knopf steht in jeder Zeile, tut aber überall genau dasselbe,
    // nämlich den ganzen Datensatz ablegen (`DocumentDetailComponent.speichern()` liest
    // die Nutzlast nicht). Das ist gewollt: der Redakteur soll beim Herunterscrollen
    // nicht nach oben müssen, und einen Datensatz zeilenweise zu speichern gibt es nicht.
    this.eventBusService.emit(action.event, this.reiter || true);
  }
}
